import React, { useState } from 'react'
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Collapse,
  Divider,
  Card,
  CardHeader,
  CardContent,
} from '@mui/material'
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  WorkOutline as WorkOutlineIcon,
  School as SchoolIcon,
} from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import api from '../../services/api'
import { extractErrorMessage } from '../../utils/errorHandler'
import { formatDateTimeJST } from '../../utils/dateUtils'

interface PendingSkill {
  id: number
  employee_id: string
  employee_name: string
  item_id: string
  item_name: string
  category_id: string
  category_name: string
  skill_level: 'S' | 'A' | 'B' | 'C' | 'D'
  comment: string | null
  status: string
  created_at: string
  updated_at: string
}

interface PendingCertification {
  id: number
  employee_id: string
  employee_name: string
  certification_name: string
  issuing_organization: string | null
  acquired_date: string
  expiry_date: string | null
  certification_number: string | null
  status: string
  created_at: string
  updated_at: string
}

interface PendingProject {
  id: number
  employee_id: string
  employee_name: string
  project_name: string
  start_date: string
  end_date: string | null
  role: string | null
  responsibilities: string | null
  technologies: string | null
  project_description: string | null
  status: string
  created_at: string
  updated_at: string
}

const getSkillLevelColor = (level: string) => {
  switch (level) {
    case 'S': return '#9c27b0'
    case 'A': return '#2196f3'
    case 'B': return '#4caf50'
    case 'C': return '#ff9800'
    case 'D': return '#9e9e9e'
    default: return '#9e9e9e'
  }
}

const getSkillLevelLabel = (level: string) => {
  switch (level) {
    case 'S': return 'S - エキスパート'
    case 'A': return 'A - 指導レベル'
    case 'B': return 'B - 独力レベル'
    case 'C': return 'C - 補助レベル'
    case 'D': return 'D - 知識レベル'
    default: return level
  }
}

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
  })
}

const SkillApprovalPage: React.FC = () => {
  const queryClient = useQueryClient()

  // スキル承認関連のstate
  const [skillRejectDialogOpen, setSkillRejectDialogOpen] = useState(false)
  const [rejectingSkill, setRejectingSkill] = useState<PendingSkill | null>(null)
  const [skillRejectedReason, setSkillRejectedReason] = useState('')
  const [expandedSkillRows, setExpandedSkillRows] = useState<Set<number>>(new Set())

  // 資格承認関連のstate
  const [certRejectDialogOpen, setCertRejectDialogOpen] = useState(false)
  const [rejectingCert, setRejectingCert] = useState<PendingCertification | null>(null)
  const [certRejectedReason, setCertRejectedReason] = useState('')
  const [expandedCertRows, setExpandedCertRows] = useState<Set<number>>(new Set())

  // プロジェクト承認関連のstate
  const [projectRejectDialogOpen, setProjectRejectDialogOpen] = useState(false)
  const [rejectingProject, setRejectingProject] = useState<PendingProject | null>(null)
  const [projectRejectedReason, setProjectRejectedReason] = useState('')
  const [expandedProjectRows, setExpandedProjectRows] = useState<Set<number>>(new Set())

  // カードセクションの展開状態
  const [skillsSectionExpanded, setSkillsSectionExpanded] = useState(true)
  const [certsSectionExpanded, setCertsSectionExpanded] = useState(true)
  const [projectsSectionExpanded, setProjectsSectionExpanded] = useState(true)

  // ==================== スキル承認API ====================

  // 承認待ちスキル一覧取得
  const { data: pendingSkillsData, refetch: refetchPendingSkills, isLoading: skillsLoading } = useQuery({
    queryKey: ['pending-skills'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: PendingSkill[] }>('/skills/employee-skills/approval/pending')
      return response.data.data
    },
  })

  // スキル承認
  const approveSkillMutation = useMutation({
    mutationFn: async (id: number) => {
      return api.post(`/skills/employee-skills/${id}/approve`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-skills'] })
      toast.success('スキル情報を承認しました')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'エラーが発生しました'))
    },
  })

  // スキル差し戻し
  const rejectSkillMutation = useMutation({
    mutationFn: async ({ id, rejected_reason }: { id: number; rejected_reason: string }) => {
      return api.post(`/skills/employee-skills/${id}/reject`, { rejected_reason })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-skills'] })
      toast.success('スキル情報を差し戻しました')
      handleCloseSkillRejectDialog()
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'エラーが発生しました'))
    },
  })

  // ==================== 資格承認API ====================

  // 承認待ち資格一覧取得
  const { data: pendingCertsData, refetch: refetchPendingCerts, isLoading: certsLoading } = useQuery({
    queryKey: ['pending-certifications'],
    queryFn: async () => {
      const response = await api.get('/employee-certifications/approval/pending')
      return response.data
    },
  })

  // 資格承認
  const approveCertMutation = useMutation({
    mutationFn: async (id: number) => {
      return api.post(`/employee-certifications/${id}/approve`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-certifications'] })
      toast.success('資格情報を承認しました')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'エラーが発生しました')
    },
  })

  // 資格差し戻し
  const rejectCertMutation = useMutation({
    mutationFn: async ({ id, rejected_reason }: { id: number; rejected_reason: string }) => {
      return api.post(`/employee-certifications/${id}/reject`, { rejected_reason })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-certifications'] })
      toast.success('資格情報を差し戻しました')
      handleCloseCertRejectDialog()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'エラーが発生しました')
    },
  })

  // ==================== プロジェクト承認API ====================

  // 承認待ちプロジェクト一覧取得
  const { data: pendingProjectsData, refetch: refetchPendingProjects, isLoading: projectsLoading } = useQuery({
    queryKey: ['pending-projects'],
    queryFn: async () => {
      const response = await api.get('/employee-projects/approval/pending')
      return response.data
    },
  })

  // プロジェクト承認
  const approveProjectMutation = useMutation({
    mutationFn: async (id: number) => {
      return api.post(`/employee-projects/${id}/approve`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-projects'] })
      toast.success('プロジェクト歴を承認しました')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'エラーが発生しました')
    },
  })

  // プロジェクト差し戻し
  const rejectProjectMutation = useMutation({
    mutationFn: async ({ id, rejected_reason }: { id: number; rejected_reason: string }) => {
      return api.post(`/employee-projects/${id}/reject`, { rejected_reason })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-projects'] })
      toast.success('プロジェクト歴を差し戻しました')
      handleCloseProjectRejectDialog()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'エラーが発生しました')
    },
  })

  // ==================== スキル承認ハンドラー ====================

  const handleApproveSkill = (skill: PendingSkill) => {
    if (window.confirm(`${skill.employee_name}さんのスキル「${skill.item_name}」を承認しますか？`)) {
      approveSkillMutation.mutate(skill.id)
    }
  }

  const handleOpenSkillRejectDialog = (skill: PendingSkill) => {
    setRejectingSkill(skill)
    setSkillRejectedReason('')
    setSkillRejectDialogOpen(true)
  }

  const handleCloseSkillRejectDialog = () => {
    setSkillRejectDialogOpen(false)
    setRejectingSkill(null)
    setSkillRejectedReason('')
  }

  const handleConfirmSkillReject = () => {
    if (!skillRejectedReason.trim()) {
      toast.error('差し戻し理由を入力してください')
      return
    }

    if (rejectingSkill) {
      rejectSkillMutation.mutate({
        id: rejectingSkill.id,
        rejected_reason: skillRejectedReason,
      })
    }
  }

  const toggleSkillRowExpand = (id: number) => {
    setExpandedSkillRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  // ==================== 資格承認ハンドラー ====================

  const handleApproveCert = (cert: PendingCertification) => {
    if (window.confirm(`${cert.employee_name}さんの資格「${cert.certification_name}」を承認しますか？`)) {
      approveCertMutation.mutate(cert.id)
    }
  }

  const handleOpenCertRejectDialog = (cert: PendingCertification) => {
    setRejectingCert(cert)
    setCertRejectedReason('')
    setCertRejectDialogOpen(true)
  }

  const handleCloseCertRejectDialog = () => {
    setCertRejectDialogOpen(false)
    setRejectingCert(null)
    setCertRejectedReason('')
  }

  const handleConfirmCertReject = () => {
    if (!certRejectedReason.trim()) {
      toast.error('差し戻し理由を入力してください')
      return
    }

    if (rejectingCert) {
      rejectCertMutation.mutate({
        id: rejectingCert.id,
        rejected_reason: certRejectedReason,
      })
    }
  }

  const toggleCertRowExpand = (id: number) => {
    setExpandedCertRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  // ==================== プロジェクト承認ハンドラー ====================

  const handleApproveProject = (project: PendingProject) => {
    if (window.confirm(`${project.employee_name}さんのプロジェクト「${project.project_name}」を承認しますか？`)) {
      approveProjectMutation.mutate(project.id)
    }
  }

  const handleOpenProjectRejectDialog = (project: PendingProject) => {
    setRejectingProject(project)
    setProjectRejectedReason('')
    setProjectRejectDialogOpen(true)
  }

  const handleCloseProjectRejectDialog = () => {
    setProjectRejectDialogOpen(false)
    setRejectingProject(null)
    setProjectRejectedReason('')
  }

  const handleConfirmProjectReject = () => {
    if (!projectRejectedReason.trim()) {
      toast.error('差し戻し理由を入力してください')
      return
    }

    if (rejectingProject) {
      rejectProjectMutation.mutate({
        id: rejectingProject.id,
        rejected_reason: projectRejectedReason,
      })
    }
  }

  const toggleProjectRowExpand = (id: number) => {
    setExpandedProjectRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleRefreshAll = () => {
    refetchPendingSkills()
    refetchPendingCerts()
    refetchPendingProjects()
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          スキル情報承認
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefreshAll}
        >
          全て更新
        </Button>
      </Box>

      {/* スキル承認セクション */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<WorkOutlineIcon />}
          title="スキル情報"
          subheader={`承認待ち: ${pendingSkillsData?.length || 0}件`}
          action={
            <IconButton
              onClick={() => setSkillsSectionExpanded(!skillsSectionExpanded)}
              sx={{
                transform: skillsSectionExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s'
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          }
          sx={{ cursor: 'pointer' }}
          onClick={() => setSkillsSectionExpanded(!skillsSectionExpanded)}
        />
        <Collapse in={skillsSectionExpanded} timeout="auto" unmountOnExit>
          <CardContent>
          {skillsLoading && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography>読み込み中...</Typography>
            </Box>
          )}

          {!skillsLoading && pendingSkillsData && pendingSkillsData.length === 0 && (
            <Alert severity="info">
              承認待ちのスキル情報はありません
            </Alert>
          )}

          {!skillsLoading && pendingSkillsData && pendingSkillsData.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="50"></TableCell>
                    <TableCell>社員名</TableCell>
                    <TableCell>カテゴリ</TableCell>
                    <TableCell>スキル項目</TableCell>
                    <TableCell>レベル</TableCell>
                    <TableCell>申請日時</TableCell>
                    <TableCell align="center">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingSkillsData.map((skill) => (
                    <React.Fragment key={skill.id}>
                      <TableRow>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleSkillRowExpand(skill.id)}
                          >
                            {expandedSkillRows.has(skill.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{skill.employee_name}</TableCell>
                        <TableCell>{skill.category_name}</TableCell>
                        <TableCell>{skill.item_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={skill.skill_level}
                            size="small"
                            sx={{ bgcolor: getSkillLevelColor(skill.skill_level), color: 'white' }}
                          />
                        </TableCell>
                        <TableCell>{formatDateTimeJST(skill.updated_at, true)}</TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<ApproveIcon />}
                            onClick={() => handleApproveSkill(skill)}
                            sx={{ mr: 1 }}
                          >
                            承認
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<RejectIcon />}
                            onClick={() => handleOpenSkillRejectDialog(skill)}
                          >
                            差し戻し
                          </Button>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={7} style={{ paddingBottom: 0, paddingTop: 0 }}>
                          <Collapse in={expandedSkillRows.has(skill.id)} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Typography variant="subtitle2" gutterBottom>
                                詳細情報
                              </Typography>
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>社員ID:</strong> {skill.employee_id}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>スキルレベル:</strong> {getSkillLevelLabel(skill.skill_level)}
                                </Typography>
                                {skill.comment && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    <strong>実務経験の詳細:</strong><br />
                                    {skill.comment}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          </CardContent>
        </Collapse>
      </Card>

      {/* 資格情報承認セクション */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<SchoolIcon />}
          title="資格情報"
          subheader={`承認待ち: ${pendingCertsData?.length || 0}件`}
          action={
            <IconButton
              onClick={() => setCertsSectionExpanded(!certsSectionExpanded)}
              sx={{
                transform: certsSectionExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s'
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          }
          sx={{ cursor: 'pointer' }}
          onClick={() => setCertsSectionExpanded(!certsSectionExpanded)}
        />
        <Collapse in={certsSectionExpanded} timeout="auto" unmountOnExit>
          <CardContent>
          {certsLoading && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography>読み込み中...</Typography>
            </Box>
          )}

          {!certsLoading && pendingCertsData && pendingCertsData.length === 0 && (
            <Alert severity="info">
              承認待ちの資格情報はありません
            </Alert>
          )}

          {!certsLoading && pendingCertsData && pendingCertsData.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="50"></TableCell>
                    <TableCell>社員名</TableCell>
                    <TableCell>資格名</TableCell>
                    <TableCell>発行機関</TableCell>
                    <TableCell>取得年月</TableCell>
                    <TableCell>有効期限</TableCell>
                    <TableCell>申請日時</TableCell>
                    <TableCell align="center">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingCertsData.map((cert: any) => (
                    <React.Fragment key={cert.id}>
                      <TableRow>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleCertRowExpand(cert.id)}
                          >
                            {expandedCertRows.has(cert.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{cert.employee_name}</TableCell>
                        <TableCell>{cert.certification_name}</TableCell>
                        <TableCell>{cert.issuing_organization || '-'}</TableCell>
                        <TableCell>{formatDate(cert.acquired_date)}</TableCell>
                        <TableCell>{formatDate(cert.expiry_date)}</TableCell>
                        <TableCell>{formatDateTimeJST(cert.updated_at, true)}</TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<ApproveIcon />}
                            onClick={() => handleApproveCert(cert)}
                            sx={{ mr: 1 }}
                          >
                            承認
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<RejectIcon />}
                            onClick={() => handleOpenCertRejectDialog(cert)}
                          >
                            差し戻し
                          </Button>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} style={{ paddingBottom: 0, paddingTop: 0 }}>
                          <Collapse in={expandedCertRows.has(cert.id)} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Typography variant="subtitle2" gutterBottom>
                                詳細情報
                              </Typography>
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>社員ID:</strong> {cert.employee_id}
                                </Typography>
                                {cert.certification_number && (
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>資格番号:</strong> {cert.certification_number}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          </CardContent>
        </Collapse>
      </Card>

      {/* プロジェクト歴承認セクション */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<WorkOutlineIcon />}
          title="プロジェクト歴"
          subheader={`承認待ち: ${pendingProjectsData?.length || 0}件`}
          action={
            <IconButton
              onClick={() => setProjectsSectionExpanded(!projectsSectionExpanded)}
              sx={{
                transform: projectsSectionExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s'
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          }
          sx={{ cursor: 'pointer' }}
          onClick={() => setProjectsSectionExpanded(!projectsSectionExpanded)}
        />
        <Collapse in={projectsSectionExpanded} timeout="auto" unmountOnExit>
          <CardContent>
          {projectsLoading && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography>読み込み中...</Typography>
            </Box>
          )}

          {!projectsLoading && pendingProjectsData && pendingProjectsData.length === 0 && (
            <Alert severity="info">
              承認待ちのプロジェクト歴はありません
            </Alert>
          )}

          {!projectsLoading && pendingProjectsData && pendingProjectsData.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="50"></TableCell>
                    <TableCell>社員名</TableCell>
                    <TableCell>プロジェクト名</TableCell>
                    <TableCell>期間</TableCell>
                    <TableCell>役割</TableCell>
                    <TableCell>使用技術</TableCell>
                    <TableCell>申請日時</TableCell>
                    <TableCell align="center">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingProjectsData.map((project: any) => (
                    <React.Fragment key={project.id}>
                      <TableRow>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleProjectRowExpand(project.id)}
                          >
                            {expandedProjectRows.has(project.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{project.employee_name}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {project.project_name}
                          </Typography>
                          {project.project_description && (
                            <Typography variant="caption" color="text.secondary">
                              {project.project_description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {formatDate(project.start_date)} ~ {project.end_date ? formatDate(project.end_date) : '現在'}
                        </TableCell>
                        <TableCell>{project.role || '-'}</TableCell>
                        <TableCell>{project.technologies || '-'}</TableCell>
                        <TableCell>{formatDateTimeJST(project.updated_at, true)}</TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<ApproveIcon />}
                            onClick={() => handleApproveProject(project)}
                            sx={{ mr: 1 }}
                          >
                            承認
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<RejectIcon />}
                            onClick={() => handleOpenProjectRejectDialog(project)}
                          >
                            差し戻し
                          </Button>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} style={{ paddingBottom: 0, paddingTop: 0 }}>
                          <Collapse in={expandedProjectRows.has(project.id)} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Typography variant="subtitle2" gutterBottom>
                                詳細情報
                              </Typography>
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>社員ID:</strong> {project.employee_id}
                                </Typography>
                                {project.responsibilities && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    <strong>担当業務詳細:</strong><br />
                                    {project.responsibilities}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          </CardContent>
        </Collapse>
      </Card>

      {/* スキル差し戻しダイアログ */}
      <Dialog open={skillRejectDialogOpen} onClose={handleCloseSkillRejectDialog} maxWidth="sm" fullWidth>
        <DialogTitle>スキル情報の差し戻し</DialogTitle>
        <DialogContent>
          {rejectingSkill && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  社員: {rejectingSkill.employee_name}<br />
                  スキル: {rejectingSkill.item_name}
                </Typography>
              </Alert>
              <TextField
                label="差し戻し理由"
                multiline
                rows={4}
                fullWidth
                value={skillRejectedReason}
                onChange={(e) => setSkillRejectedReason(e.target.value)}
                helperText="差し戻しの理由を詳しく記入してください"
                required
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSkillRejectDialog}>キャンセル</Button>
          <Button onClick={handleConfirmSkillReject} color="warning" variant="contained">
            差し戻し
          </Button>
        </DialogActions>
      </Dialog>

      {/* 資格差し戻しダイアログ */}
      <Dialog open={certRejectDialogOpen} onClose={handleCloseCertRejectDialog} maxWidth="sm" fullWidth>
        <DialogTitle>資格情報の差し戻し</DialogTitle>
        <DialogContent>
          {rejectingCert && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  社員: {rejectingCert.employee_name}<br />
                  資格: {rejectingCert.certification_name}
                </Typography>
              </Alert>
              <TextField
                label="差し戻し理由"
                multiline
                rows={4}
                fullWidth
                value={certRejectedReason}
                onChange={(e) => setCertRejectedReason(e.target.value)}
                helperText="差し戻しの理由を詳しく記入してください"
                required
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCertRejectDialog}>キャンセル</Button>
          <Button onClick={handleConfirmCertReject} color="warning" variant="contained">
            差し戻し
          </Button>
        </DialogActions>
      </Dialog>

      {/* プロジェクト差し戻しダイアログ */}
      <Dialog open={projectRejectDialogOpen} onClose={handleCloseProjectRejectDialog} maxWidth="sm" fullWidth>
        <DialogTitle>プロジェクト歴の差し戻し</DialogTitle>
        <DialogContent>
          {rejectingProject && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  社員: {rejectingProject.employee_name}<br />
                  プロジェクト: {rejectingProject.project_name}
                </Typography>
              </Alert>
              <TextField
                label="差し戻し理由"
                multiline
                rows={4}
                fullWidth
                value={projectRejectedReason}
                onChange={(e) => setProjectRejectedReason(e.target.value)}
                helperText="差し戻しの理由を詳しく記入してください"
                required
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProjectRejectDialog}>キャンセル</Button>
          <Button onClick={handleConfirmProjectReject} color="warning" variant="contained">
            差し戻し
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default SkillApprovalPage
